import axios from "axios";
import { auth } from "../../core/auth";
import { env } from "../../env";

/*
 * Client for the EVV "Need dev help" endpoints.
 *
 * Called through plain axios rather than the typed `api` client because the
 * endpoints are not in the checked-in generated schema yet -- the same situation
 * as the Sandata bulk re-submit modal. Once `pnpm openapi:local` is re-run against
 * a server on the evv-dev-help branch, the local mirrors below
 * can be replaced by the generated `Messages` types. Dates arrive as ISO strings.
 */

/** Mirrors EVV_DEV_HELP_MAX_VISITS_PER_REQUEST in the server's src/messages/EvvDevHelp.ts. */
export const EVV_DEV_HELP_MAX_VISITS_PER_REQUEST = 500;

export type EvvDevHelpReasonCategory =
  | "DATA_ISSUE"
  | "OLD_VISIT"
  | "RESUBMIT_KEEPS_FAILING"
  | "OTHER";

export type EvvDevHelpVisitStatus = "AWAITING_DEV" | "HANDLED";

export type EvvDevHelpCreateRequestParams = {
  visitInstanceIds: number[];
  reasonText: string;
};

export type EvvDevHelpCreateRequestResponse = {
  requestId: number | null;
  flaggedVisitInstanceIds: number[];
  alreadyOpenVisitInstanceIds: number[];
};

export type EvvDevHelpVisitStatusEntry = {
  visitInstanceId: number;
  status: EvvDevHelpVisitStatus;
  reasonCategory: EvvDevHelpReasonCategory;
  flaggedAt: string;
  handledAt: string | null;
  handledNote: string | null;
};

export type EvvDevHelpDashboardRequest = {
  status: EvvDevHelpVisitStatus | "ALL";
  officeId: number | null;
  aggregator: string | null;
  errorSearch: string | null;
  limit: number;
  offset: number;
};

export type EvvDevHelpDashboardVisit = {
  visitInstanceId: number;
  visitStartTime: string;
  aggregator: string;
  errorSnapshot: string[];
  status: EvvDevHelpVisitStatus;
  handledAt: string | null;
  handledBy: number | null;
  handledByName: string | null;
  handledNote: string | null;
};

export type EvvDevHelpDashboardEntry = {
  id: number;
  officeId: number;
  officeName: string;
  reasonCategory: EvvDevHelpReasonCategory;
  reasonText: string;
  createdAt: string;
  createdBy: number;
  createdByName: string;
  visits: EvvDevHelpDashboardVisit[];
};

export type EvvDevHelpDashboardResponse = {
  requests: EvvDevHelpDashboardEntry[];
  offices: { id: number; name: string }[];
  totalRequests: number;
};

function authHeaders() {
  return { Authorization: `Token ${auth.getAuthOrFail().tokens.authToken}` };
}

function agencyPath(path: string) {
  const authState = auth.getAuthOrFail();
  return (
    `${env.API_URL}agencies/${authState.data.agency.id}` +
    `/agency_members/${authState.data.agencyMember.id}/${path}`
  );
}

/** `enabled`: the agency has the feature. `canManage`: this member may use the dashboard. */
export async function getEvvDevHelpAvailability() {
  const response = await axios.get<{ enabled: boolean; canManage: boolean }>(
    agencyPath("evv_dev_help_requests/availability"),
    { headers: authHeaders() }
  );
  return response.data;
}

export async function createEvvDevHelpRequest(params: EvvDevHelpCreateRequestParams) {
  const response = await axios.post<EvvDevHelpCreateRequestResponse>(
    agencyPath("evv_dev_help_requests"),
    params,
    { headers: authHeaders() }
  );
  return response.data;
}

export async function getEvvDevHelpVisitStatuses(visitInstanceIds: number[]) {
  const response = await axios.post<{ statuses: EvvDevHelpVisitStatusEntry[] }>(
    agencyPath("evv_dev_help_requests/visit_statuses"),
    { visitInstanceIds },
    { headers: authHeaders() }
  );
  return response.data.statuses;
}

export async function searchEvvDevHelpRequests(params: EvvDevHelpDashboardRequest) {
  const response = await axios.post<EvvDevHelpDashboardResponse>(
    agencyPath("evv_dev_help_requests/search"),
    params,
    { headers: authHeaders() }
  );
  return response.data;
}

export async function handleEvvDevHelpVisits(params: {
  visitInstanceIds: number[];
  note: string | null;
}) {
  const response = await axios.post<{ handledVisitInstanceIds: number[] }>(
    agencyPath("evv_dev_help_requests/handle"),
    params,
    { headers: authHeaders() }
  );
  return response.data;
}

export async function exportEvvDevHelpRequests(params: EvvDevHelpDashboardRequest) {
  const response = await axios.post<{ csv: string }>(
    agencyPath("evv_dev_help_requests/export"),
    params,
    { headers: authHeaders() }
  );
  return response.data.csv;
}
